import { useEffect, useState } from "react";
import { setWhatsAppProduct } from "@/components/WhatsAppButton";
import { useParams, useNavigate } from "react-router-dom";
import { api, LOGO } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { ArrowLeft, Heart, Check, ShoppingBag, Leaf, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getRef } from "@/lib/ref";
import { CoordinatedLook } from "@/components/CoordinatedLook";
import { ProductBadges } from "@/components/LovedBadge";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToWishlist, inWishlist, profile } = useProfile();
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [buying, setBuying] = useState(false);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/catalog/product/${id}`)
      .then(({ data }) => {
        setProduct(data); setWhatsAppProduct(data.title);
        setSize(data.sizes?.length === 1 ? data.sizes[0] : null);
        setColor(data.colors_available?.length === 1 ? data.colors_available[0] : null);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  const okFor = (s, c) => !product.variants?.length
    || product.variants.some((v) => v.available && (!s || v.size === s) && (!c || v.color === c));
  const pickSize = (s) => { setSize(s); if (color && !okFor(s, color)) setColor(null); };
  const pickColor = (c) => { setColor(c); if (size && !okFor(size, c)) setSize(null); };

  const buy = async () => {
    if (product.sizes?.length > 1 && !size) return toast.error("Scegli prima la taglia");
    if (product.colors_available?.length > 1 && !color) return toast.error("Scegli prima il colore");
    setBuying(true);
    try {
      const { data } = await api.post("/checkout-product", { product_id: String(product.id), size, color, profile_id: profile?.id, ref: getRef() });
      window.location.assign(data.checkout_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossibile aprire il checkout");
      setBuying(false);
    }
  };

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-[#EAE3DC]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <button onClick={goBack} data-testid="product-back-button"
            className="inline-flex items-center gap-2 text-sm text-[#5C534E] hover:text-[#C87D75] transition-colors">
            <ArrowLeft size={18} /> Indietro
          </button>
          <button onClick={() => navigate("/")} className="flex items-center" aria-label="Home">
            <img src={LOGO} alt="Minime Baby" className="h-9 w-auto object-contain" />
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 pt-8 pb-28 lg:pb-8">
        {loading ? (
          <div className="flex items-center gap-2 text-[#8C7F78] py-24 justify-center" data-testid="product-loading">
            <Loader2 className="animate-spin" size={20} /> Caricamento prodotto...
          </div>
        ) : notFound || !product ? (
          <div className="text-center py-24" data-testid="product-not-found">
            <p className="text-[#8C7F78]">Prodotto non trovato.</p>
            <button onClick={goBack} className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#2D2825] text-white px-6 py-2.5 text-sm">
              <ArrowLeft size={16} /> Torna al catalogo
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12" data-testid="product-detail">
            <div className="relative rounded-3xl overflow-hidden bg-white border border-[#EAE3DC] aspect-square">
              {product.image ? (
                <img src={product.image} alt={product.title} className="w-full h-full object-cover" />
              ) : <div className="w-full h-full flex items-center justify-center text-[#C8BDB3]">Nessuna immagine</div>}
              <span className="absolute top-4 left-4 rounded-full bg-white/90 backdrop-blur text-[#4E7363] text-xs px-3 py-1">
                {product.category}
              </span>
              {product.gender === "neutro" && (
                <span className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full bg-[#EAF0EC] text-[#4E7363] text-xs px-3 py-1 border border-[#A7C4B5]">
                  <Leaf size={12} /> Neutro
                </span>
              )}
              <ProductBadges product={product} className="absolute bottom-4 right-4" />
              {!product.available && (
                <span className="absolute bottom-4 left-4 rounded-full bg-[#B85750] text-white text-xs px-3 py-1">Esaurito</span>
              )}
            </div>

            <div className="flex flex-col">
              <h1 className="font-serif text-3xl sm:text-4xl text-[#2D2825] leading-tight">{product.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-[#8C7F78]">
                <span className="rounded-full bg-[#F4EFEA] px-3 py-1 text-[#5C534E]">{product.material}</span>
                <span className="rounded-full bg-[#EAF0EC] px-3 py-1 text-[#4E7363]">{product.season_label}</span>
              </div>

              {product.sizes && product.sizes.length > 0 && (
                <div className="mt-5">
                  <div className="text-sm text-[#5C534E] mb-2">Scegli la taglia</div>
                  <Chips items={product.sizes} value={size} onChange={pickSize} testid="product-size" ok={(s) => okFor(s, color)} />
                </div>
              )}
              {product.colors_available?.length > 1 && (
                <div className="mt-4">
                  <div className="text-sm text-[#5C534E] mb-2">Scegli il colore</div>
                  <Chips items={product.colors_available} value={color} onChange={pickColor} testid="product-color" ok={(c) => okFor(size, c)} />
                </div>
              )}

              <div className="mt-6 font-serif text-4xl text-[#C87D75]">
                {product.price ? `€ ${product.price.replace(".", ",")}` : "—"}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  data-testid="product-add-to-list" onClick={() => addToWishlist(product)}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-[#E5A8A0] text-[#C87D75] hover:bg-[#F9ECE9] py-3 transition-colors"
                >
                  {inWishlist(product.id) ? <><Check size={18} /> Nella tua lista</> : <><Heart size={18} /> Aggiungi alla lista</>}
                </button>
                <button
                  data-testid="product-buy" onClick={buy} disabled={buying || !product.available}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#2D2825] text-white hover:bg-[#473f3a] disabled:opacity-50 py-3 transition-colors"
                >
                  {buying ? <Loader2 className="animate-spin" size={16} /> : <ShoppingBag size={16} />} {product.available ? "Acquista" : "Esaurito"}
                </button>
              </div>

              <div className="mt-5 rounded-xl bg-[#FDF6E7] border border-[#F2D293] p-4 text-sm text-[#7a5f1f]">
                {profile ? <>Il tuo <strong>sconto personale del 15%</strong> si applica in automatico al checkout.</>
                  : <>Crea il tuo profilo mamma per avere lo <strong>sconto personale del 15%</strong> su tutto.</>}
              </div>

              <button onClick={goBack} data-testid="product-back-bottom"
                className="mt-6 inline-flex items-center gap-2 text-sm text-[#8C7F78] hover:text-[#2D2825] transition-colors self-start">
                <ArrowLeft size={16} /> Torna indietro
              </button>
            </div>
          </div>
        )}
      </main>
      {product && <CoordinatedLook productId={String(product.id)} heading="Completa il look" testId="complete-the-look" />}
    </div>
  );
}

const Chips = ({ items, value, onChange, testid, ok }) => (
  <div className="flex flex-wrap gap-2" data-testid={`${testid}s`}>
    {items.map((s) => (
      <button key={s} type="button" onClick={() => onChange(s)} data-testid={`${testid}-${s}`}
        title={ok(s) ? undefined : "Non disponibile con la scelta fatta: toccalo per cambiare"}
        className={`rounded-lg border text-sm px-3 py-1.5 transition-colors ${value === s ? "border-[#2D2825] bg-[#2D2825] text-white"
          : ok(s) ? "border-[#EAE3DC] bg-white text-[#5C534E] hover:border-[#C8BDB3]" : "border-dashed border-[#E5DED7] bg-transparent text-[#C8BDB3] line-through"}`}>
        {s}
      </button>
    ))}
  </div>
);
